// Browser go-live: capture the DJ's mic or a tab's audio and stream
// MediaRecorder chunks to /api/broadcast over a same-origin WebSocket
// (session cookie = auth). The server transcodes onto the club's Icecast
// mount — from there it's the ordinary radio pipeline.

import { DEFAULT_SERVER_PORT } from "@bebop/shared";

export type BroadcastSource = "mic" | "tab";
export type BroadcastState = "idle" | "connecting" | "live" | "error";

/** Smaller chunks = smoother delivery through jittery uplinks. */
const CHUNK_MS = 250;
/** Warn the DJ when the source has been this quiet for this long. */
const SILENCE_RMS = 0.001;
const SILENCE_AFTER_MS = 5000;

function pickMimeType(): string | undefined {
  for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return undefined;
}

export class Broadcaster {
  state: BroadcastState = "idle";
  /** Human-readable detail for the error state. */
  errorText = "";

  onChange: (() => void) | undefined;

  private ws: WebSocket | undefined;
  private recorder: MediaRecorder | undefined;
  private tracks: MediaStreamTrack[] = [];
  private stoppedByUs = false;

  // Source health: an analyser on the capture stream so the DJ finds out
  // their source went quiet from the booth UI, not from their listeners.
  private monitorCtx: AudioContext | undefined;
  /** Retained on purpose — an unreferenced MediaStreamAudioSourceNode gets GC'd and the analyser goes flat. */
  private monitorSource: MediaStreamAudioSourceNode | undefined;
  private analyser: AnalyserNode | undefined;
  private analyserBuf: Uint8Array<ArrayBuffer> | undefined;
  private quietSinceMs: number | undefined;
  // Keep the screen awake while on air — display sleep kills capture.
  private wakeLock: { release(): Promise<void> } | undefined;
  private onVisible = () => {
    if (document.visibilityState === "visible" && this.state === "live") {
      void this.acquireWakeLock();
    }
  };

  get isLive(): boolean {
    return this.state === "live";
  }

  /** Acquire the chosen source and go live in `clubId`. */
  async start(clubId: string, source: BroadcastSource): Promise<void> {
    if (this.state !== "idle" && this.state !== "error") return;
    let stream: MediaStream;
    try {
      if (source === "mic") {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
      } else {
        // Tab/system audio arrives via display capture; the video track is
        // required by the API but never leaves the machine.
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });
        if (stream.getAudioTracks().length === 0) {
          for (const t of stream.getTracks()) t.stop();
          this.fail('no audio in that capture — pick a tab and tick "also share tab audio"');
          return;
        }
      }
    } catch {
      this.fail("capture was blocked or cancelled");
      return;
    }
    this.startWithStream(clubId, stream);
  }

  /**
   * Go live from an existing MediaStream (the UI uses start(); this seam
   * also lets dev tooling broadcast a synthetic stream).
   */
  startWithStream(clubId: string, stream: MediaStream): void {
    this.setState("connecting");
    const mimeType = pickMimeType();
    if (!mimeType) {
      for (const t of stream.getTracks()) t.stop();
      this.fail("this browser can't encode a live stream — use Chrome or Firefox");
      return;
    }

    this.tracks = stream.getTracks();
    const audioOnly = new MediaStream(stream.getAudioTracks());
    // Same-origin in production (Caddy routes /api upgrades to the API
    // port); direct in dev — Vite's own HMR socket swallows proxied
    // upgrades, and localhost cookies are port-agnostic anyway.
    const protocol = location.protocol === "https:" ? "wss" : "ws";
    const url = import.meta.env.DEV
      ? `ws://${location.hostname}:${DEFAULT_SERVER_PORT + 1}/api/broadcast`
      : `${protocol}://${location.host}/api/broadcast`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    this.stoppedByUs = false;

    ws.addEventListener("open", () => ws.send(clubId));
    ws.addEventListener("message", (event) => {
      if (event.data === "live" && this.state === "connecting") {
        const recorder = new MediaRecorder(audioOnly, {
          mimeType,
          audioBitsPerSecond: 128_000,
        });
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) ws.send(e.data);
        };
        recorder.start(CHUNK_MS);
        this.recorder = recorder;
        this.startSourceMonitor(audioOnly);
        void this.acquireWakeLock();
        document.addEventListener("visibilitychange", this.onVisible);
        this.setState("live");
      }
    });
    ws.addEventListener("close", (event) => {
      this.teardown();
      if (this.stoppedByUs) {
        this.setState("idle");
      } else {
        this.fail(event.reason || "the stream dropped");
      }
    });

    // The capture source vanishing (tab closed, mic unplugged, "stop
    // sharing" clicked) ends the set cleanly.
    for (const track of this.tracks) {
      track.addEventListener("ended", () => this.stop());
    }
  }

  /** End the set. */
  stop(): void {
    if (this.state !== "live" && this.state !== "connecting") return;
    this.stoppedByUs = true;
    this.recorder?.stop();
    this.ws?.close();
    this.teardown();
    this.setState("idle");
  }

  /** Leave the error state (after showing the message). */
  clearError(): void {
    if (this.state === "error") this.setState("idle");
  }

  /**
   * True while on air but the captured source has been silent for a few
   * seconds — the DJ's cue that their tab/mic died, before listeners tell
   * them. Sampled by the UI every frame.
   */
  sourceSilent(): boolean {
    if (this.state !== "live" || !this.analyser || !this.analyserBuf) return false;
    this.analyser.getByteTimeDomainData(this.analyserBuf);
    let sum = 0;
    for (const v of this.analyserBuf) {
      const centered = (v - 128) / 128;
      sum += centered * centered;
    }
    const rms = Math.sqrt(sum / this.analyserBuf.length);
    const now = performance.now();
    if (rms > SILENCE_RMS) {
      this.quietSinceMs = undefined;
      return false;
    }
    this.quietSinceMs ??= now;
    return now - this.quietSinceMs > SILENCE_AFTER_MS;
  }

  private startSourceMonitor(stream: MediaStream): void {
    this.monitorCtx = new AudioContext();
    void this.monitorCtx.resume();
    this.monitorSource = this.monitorCtx.createMediaStreamSource(stream);
    this.analyser = this.monitorCtx.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyserBuf = new Uint8Array(this.analyser.frequencyBinCount);
    this.monitorSource.connect(this.analyser);
    // A muted tap to the destination keeps the graph rendering — a
    // dead-end analyser subgraph is eligible for GC and goes flat.
    const mute = this.monitorCtx.createGain();
    mute.gain.value = 0;
    this.analyser.connect(mute);
    mute.connect(this.monitorCtx.destination);
    this.quietSinceMs = undefined;
  }

  private async acquireWakeLock(): Promise<void> {
    try {
      const nav = navigator as Navigator & {
        wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void> }> };
      };
      this.wakeLock = await nav.wakeLock?.request("screen");
    } catch {
      // Not supported or denied — the set still works, sleep just isn't held off.
    }
  }

  private teardown(): void {
    for (const track of this.tracks) track.stop();
    this.tracks = [];
    this.recorder = undefined;
    this.ws = undefined;
    void this.monitorCtx?.close();
    this.monitorCtx = undefined;
    this.monitorSource = undefined;
    this.analyser = undefined;
    this.analyserBuf = undefined;
    this.quietSinceMs = undefined;
    void this.wakeLock?.release().catch(() => {});
    this.wakeLock = undefined;
    document.removeEventListener("visibilitychange", this.onVisible);
  }

  private fail(text: string): void {
    this.teardown();
    this.errorText = text;
    this.setState("error");
  }

  private setState(state: BroadcastState): void {
    this.state = state;
    this.onChange?.();
  }
}
